import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Download, Trash2, MapPin, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";

export const USDAZoneImporter = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [isImportingOphz, setIsImportingOphz] = useState(false);
  const [ophzStatus, setOphzStatus] = useState<string>("");

  const handleDownloadUSDAData = async () => {
    setIsDownloading(true);
    try {
      const { data, error } = await supabase.functions.invoke("import-usda-zones", {
        body: { 
          action: "download_and_import",
          replace_existing: replaceExisting 
        },
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: data?.message || "USDA hardiness zone data imported successfully.",
      });

    } catch (err: any) {
      console.error("USDA zone import error:", err);

      // Try one automatic retry (the Edge Function will attempt a fallback source)
      try {
        const { data, error } = await supabase.functions.invoke("import-usda-zones", {
          body: { 
            action: "download_and_import",
            replace_existing: replaceExisting 
          },
        });
        if (error) throw error;
        toast({ title: "Success", description: data?.message || "USDA hardiness zone data imported successfully (fallback)." });
        return;
      } catch (retryErr: any) {
        toast({
          title: "Error",
          description: `Failed to import USDA zones: ${retryErr?.message || retryErr}`,
          variant: "destructive",
        });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClearAllZones = async () => {
    if (!confirm("Are you sure you want to delete ALL USDA zone polygons? This action cannot be undone.")) {
      return;
    }

    setIsClearing(true);
    try {
      const { data, error } = await supabase.functions.invoke("import-usda-zones", {
        method: "DELETE",
        body: { action: "clear_all" },
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: data?.message || "All USDA zone polygons have been cleared successfully.",
      });
    } catch (error: any) {
      console.error('Clear USDA zones error:', error);
      toast({
        title: "Error",
        description: `Failed to clear USDA zones: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  const handleImportOPHZ = async () => {
    if (isImportingOphz) return;
    setIsImportingOphz(true);
    setOphzStatus("Starting OPHZ import...");

    const states = ['AL','AZ','AR','CA','CO','CT','DC','DE','FL','GA','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VA','VT','WA','WI','WV','WY'];

    let totalInserted = 0;
    try {
      for (let i = 0; i < states.length; i++) {
        const s = states[i];
        setOphzStatus(`Importing ${s} (${i + 1}/${states.length})...`);
        const { data, error } = await supabase.functions.invoke("import-usda-zones", {
          body: {
            action: "import_ophz_state",
            state: s,
            truncate_before: replaceExisting && i === 0,
          },
        });
        if (error) throw error;
        totalInserted += data?.polygons_inserted || 0;
      }

      toast({ title: "OPHZ import complete", description: `Inserted ~${totalInserted} polygons across ${states.length} states.` });
    } catch (e: any) {
      console.error("OPHZ import error:", e);
      toast({ title: "OPHZ import failed", description: e?.message || String(e), variant: "destructive" });
    } finally {
      setIsImportingOphz(false);
      setOphzStatus("");
    }
  };
  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          USDA Hardiness Zone Importer
        </CardTitle>
        <CardDescription>
          Import official USDA hardiness zone polygon data for nationwide accurate zone detection
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This will download and import the official 2023 USDA Plant Hardiness Zone Map data 
            (~50MB GeoJSON). This enables precise point-in-polygon zone detection nationwide.
          </AlertDescription>
        </Alert>

        <div className="flex items-center space-x-2">
          <Checkbox 
            id="replace-existing" 
            checked={replaceExisting}
            onCheckedChange={(checked) => setReplaceExisting(checked as boolean)}
          />
          <label 
            htmlFor="replace-existing" 
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Replace existing polygons (clear table first)
          </label>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={handleDownloadUSDAData} 
            disabled={isDownloading}
            className="flex-1 min-w-[220px] flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {isDownloading ? "Downloading..." : "Download Official USDA Data"}
          </Button>

          <Button 
            onClick={handleImportOPHZ}
            disabled={isImportingOphz}
            className="flex-1 min-w-[220px] flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {isImportingOphz ? (ophzStatus || "Importing OPHZ...") : "Import OPHZ (contiguous US)"}
          </Button>

          <Button 
            onClick={handleClearAllZones}
            disabled={isClearing}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {isClearing ? "Clearing..." : "Clear All"}
          </Button>
        </div>

        <div className="text-sm text-muted-foreground bg-muted p-3 rounded">
          <strong>What this does:</strong>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>Downloads official USDA 2023 hardiness zone polygons from ArcGIS</li>
            <li>Imports precise polygon boundaries for all US zones (1a-13b)</li>
            <li>Enables accurate GPS → zone lookups using PostGIS spatial queries</li>
            <li>Replaces the current latitude-based approximation with official data</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};